import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from '../contexts/ChatContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { UserPlus, X, Search, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChannelMembersDialog = ({ open, onClose, channelId }) => {
  const { onlineUsers } = useChat();
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showAddPanel, setShowAddPanel] = useState(false);

  const fetchMembers = useCallback(async () => {
    if (!channelId) return;
    try {
      const res = await axios.get(`${API}/channels/${channelId}/members`);
      setMembers(res.data.members);
    } catch (e) { console.error(e); }
  }, [channelId]);

  const fetchAllUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setAllUsers(res.data.users);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (open) {
      fetchMembers();
      fetchAllUsers();
    }
  }, [open, fetchMembers, fetchAllUsers]);

  const addMember = async (userId) => {
    try {
      await axios.post(`${API}/channels/${channelId}/members`, { user_id: userId });
      toast.success('Member added');
      fetchMembers();
    } catch (e) {
      toast.error('Failed to add member');
    }
  };

  const removeMember = async (userId) => {
    try {
      await axios.delete(`${API}/channels/${channelId}/members/${userId}`);
      toast.success('Member removed');
      fetchMembers();
    } catch (e) {
      toast.error('Failed to remove member');
    }
  };

  const memberIds = new Set(members.map(m => m.user_id));
  const nonMembers = allUsers.filter(u => !memberIds.has(u.user_id) && u.username?.toLowerCase().includes(search.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="channel-members-dialog">
        <DialogHeader>
          <DialogTitle style={{fontFamily: 'Space Grotesk'}} className="flex items-center gap-2">
            <Users className="w-5 h-5" /> Channel Members
          </DialogTitle>
          <DialogDescription>{members.length} member{members.length !== 1 ? 's' : ''}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-60">
          <div className="space-y-1">
            {members.map(m => (
              <div key={m.user_id} className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-[hsl(var(--surface-2))]" data-testid={`member-row-${m.user_id}`}>
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <div className="w-7 h-7 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-xs font-semibold">
                      {m.display_name?.[0] || m.username?.[0] || '?'}
                    </div>
                    {onlineUsers[m.user_id] && <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[hsl(var(--presence-online))] border border-card" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{m.display_name || m.username}</p>
                    <p className="text-xs text-muted-foreground">{m.title || m.department || '@' + m.username}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeMember(m.user_id)} data-testid={`remove-member-${m.user_id}`}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>

        <Separator />

        {showAddPanel ? (
          <div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-sm" data-testid="channel-members-search" />
            </div>
            <ScrollArea className="max-h-40">
              <div className="space-y-1">
                {nonMembers.slice(0, 10).map(u => (
                  <div key={u.user_id} className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-[hsl(var(--surface-2))]">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-[10px] font-semibold">
                        {u.display_name?.[0] || u.username?.[0]}
                      </div>
                      <span className="text-sm">{u.display_name || u.username}</span>
                    </div>
                    <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => addMember(u.user_id)} data-testid={`add-member-${u.user_id}`}>
                      Add
                    </Button>
                  </div>
                ))}
                {nonMembers.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No users to add</p>}
              </div>
            </ScrollArea>
            <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => setShowAddPanel(false)}>Done</Button>
          </div>
        ) : (
          <Button variant="outline" className="w-full" onClick={() => setShowAddPanel(true)} data-testid="channel-members-add-button">
            <UserPlus className="w-4 h-4 mr-2" /> Add Members
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChannelMembersDialog;